import { computed } from 'vue';
import { useTablesStore } from '@/stores/tables';
import { useUserStore } from '@/stores/user';
import { parseAclViewsVal, parseAclRights, hasSubViewAccess, effectiveSubViews } from '@/config/aclViews';
import type { AclViewKey } from '@/config/aclViews';

/**
 * Composable for checking user permissions based on ACL rights.
 * Admin users (role === 'admin') always pass every check.
 */
export function usePermissions() {
    const tablesStore = useTablesStore();
    const userStore = useUserStore();

    /**
     * Resolves whether the current user is an admin by checking
     * their ACL level entries in companies_acl_level.
     */
    const isAdmin = computed((): boolean => {
        // Kein Admin, solange die Rechte der aktuellen Anmeldung nicht geladen
        // sind. Ohne diese Zeile beantwortet isAdmin die Frage aus den Mappings
        // allein — nach einem Benutzerwechsel wären das kurzzeitig die des
        // vorherigen Mitarbeiters. Die Mappings kommen ohnehin in derselben
        // Worker-Nachricht wie rightsloaded = true.
        if (!tablesStore.config.rightsloaded) return false;

        const aclLevelMappings: any[] = (userStore.Userdata as any)?.ActiveUserCompanyACLLevel || [];
        if (!aclLevelMappings.length) return false;

        // Preferred: the query worker enriches each mapping with the level's
        // role — independent of the companies_acl_level store table being loaded
        if (aclLevelMappings.some((m: any) => m.role === 'admin')) return true;

        // Fallback (older cached payloads without role): look the level up in
        // the synced companies_acl_level table
        const levelIds = aclLevelMappings.map((m: any) => m.acllevelid).filter(Boolean);
        if (!levelIds.length) return false;

        const allLevels: any[] = tablesStore.tables?.companies_acl_level || [];
        return allLevels.some((level: any) => levelIds.includes(level.id) && level.role === 'admin');
    });

    /**
     * Studio-Inhaber: Traeger einer ACL-Stufe mit `role = 'admin'` oder einem
     * Namen, der „Inhaber" enthaelt (die Stufe heisst ueblicherweise
     * `StudioInhaber`). Dieselbe Definition benutzt die mgmgnt-App, um den
     * Inhaber eines Studios zu bestimmen — siehe `mgmgnt/server/company-detail.js`.
     *
     * Gedacht fuer Handlungen, die dem Betreiber selbst vorbehalten sind, etwa
     * den Abschluss des Auftragsverarbeitungsvertrags. Fuer Sichtrechte bleibt
     * `hasViewPermission()` zustaendig.
     */
    const isStudioOwner = computed((): boolean => {
        if (!tablesStore.config.rightsloaded) return false;
        if (isAdmin.value) return true;

        const aclLevelMappings: any[] = (userStore.Userdata as any)?.ActiveUserCompanyACLLevel || [];
        if (!aclLevelMappings.length) return false;

        const isOwnerName = (name: any) => typeof name === 'string' && /inhaber/i.test(name);
        if (aclLevelMappings.some((m: any) => isOwnerName(m.levelname))) return true;

        // Fallback fuer aeltere Payloads ohne angereicherten Levelnamen.
        const levelIds = aclLevelMappings.map((m: any) => m.acllevelid).filter(Boolean);
        if (!levelIds.length) return false;
        const allLevels: any[] = tablesStore.tables?.companies_acl_level || [];
        return allLevels.some((level: any) => levelIds.includes(level.id) && isOwnerName(level.name));
    });

    /**
     * Check if the current user has permission to view a specific section.
     * Admin users bypass all permission checks.
     * @param permission - AclViewKey to check (e.g. 'Kalender', 'Kunden')
     */
    const hasViewPermission = (permission: AclViewKey): boolean => {
        // Admins always have access
        if (isAdmin.value) return true;

        if (!tablesStore.config.rightsloaded) {
            return false;
        }

        const aclRights = (userStore.Userdata as any)?.ActiveUserCompanyACLRights;
        if (!aclRights || aclRights.length === 0) {
            return false;
        }

        const viewsAcl = aclRights.find((acl: any) => acl.action === 'views') as any;
        if (!viewsAcl || !viewsAcl.val) {
            return false;
        }

        return parseAclViewsVal(viewsAcl.val as string).includes(permission);
    };

    /**
     * Check if the current user has permission to view a specific sub-view
     * inside a section (e.g. hasSubViewPermission('Kunden', 'Insights')).
     *
     * Admins bypass. Non-admins must have the parent view; sub-view grants use
     * the legacy fallback in `hasSubViewAccess` (parent granted + no stored
     * sub-keys ⇒ all sub-views allowed).
     */
    const hasSubViewPermission = (parent: AclViewKey, sub: string): boolean => {
        if (isAdmin.value) return true;
        if (!tablesStore.config.rightsloaded) return false;

        const aclRights = (userStore.Userdata as any)?.ActiveUserCompanyACLRights;
        if (!aclRights || aclRights.length === 0) return false;

        const viewsAcl = aclRights.find((acl: any) => acl.action === 'views') as any;
        if (!viewsAcl || !viewsAcl.val) return false;

        return hasSubViewAccess(parseAclRights(viewsAcl.val as string), parent, sub);
    };

    /**
     * Effective sub-view keys the current user may access for a parent section.
     * Admins get every defined sub-view; non-admins get their granted set (with
     * the legacy fallback = all). Returns [] if the parent view isn't granted.
     */
    const availableSubViews = (parent: AclViewKey): string[] => {
        if (isAdmin.value) {
            // Admins get all defined sub-views
            return effectiveSubViews({ views: [parent], subViews: {} }, parent);
        }
        if (!tablesStore.config.rightsloaded) return [];

        const aclRights = (userStore.Userdata as any)?.ActiveUserCompanyACLRights;
        if (!aclRights || aclRights.length === 0) return [];

        const viewsAcl = aclRights.find((acl: any) => acl.action === 'views') as any;
        if (!viewsAcl || !viewsAcl.val) return [];

        return effectiveSubViews(parseAclRights(viewsAcl.val as string), parent);
    };

    /**
     * Reactive: whether rights loading is complete.
     */
    const rightsLoaded = computed(() => tablesStore.config.rightsloaded || false);

    /**
     * Check if the current user has edit permission for a specific section.
     * In the current ACL model, edit permissions reuse view-level checks.
     * Admins always pass.
     */
    const hasEditPermission = (permission: AclViewKey): boolean => {
        return hasViewPermission(permission);
    };

    /**
     * Reactive: allowed AclViewKeys for the current user (all keys for admins).
     */
    const availableViews = computed((): AclViewKey[] => {
        if (isAdmin.value) {
            // Admins get all views
            return ['Kalender', 'Kunden', 'Leistungen', 'Finanzen', 'Kommunikation', 'Widgets', 'Unternehmen', 'FormularProzesse'];
        }

        if (!tablesStore.config.rightsloaded) return [];

        const aclRights = (userStore.Userdata as any)?.ActiveUserCompanyACLRights;
        if (!aclRights || aclRights.length === 0) return [];

        const viewsAcl = aclRights.find((acl: any) => acl.action === 'views') as any;
        if (!viewsAcl || !viewsAcl.val) return [];

        return parseAclViewsVal(viewsAcl.val as string);
    });

    return {
        hasViewPermission,
        hasSubViewPermission,
        hasEditPermission,
        isAdmin,
        isStudioOwner,
        rightsLoaded,
        availableViews,
        availableSubViews
    };
}
